import ProductImage, { type ProductImageWidths } from "~/domains/product/components/ProductImage";
import classNames from "classnames";
import ClampedText from "~/common/components/ClampedText";
import Parameter from "~/common/components/Parameter";
import { type HousingProduct, HousingProductType } from "~/domains/product/types/ProductData";
import { Link, LinkType } from "~/common/components/Link";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { Translation } from "~/app/i18n/Translation";
import { useFormContext, useWatch } from "react-hook-form";
import type { AddToCartFormDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useTranslation } from "react-i18next";

type HeadlineLevels = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";

interface Props {
  housingProducts: HousingProduct[];
  headlineLevel?: HeadlineLevels;
  className?: string;
}

export const IncludedPartsSection = ({ housingProducts, headlineLevel = "p", className }: Props) => {
  const Headline = headlineLevel;
  const { t } = useTranslation();

  const kitQuantity = useWatch({
    name: "quantity",
    control: useFormContext<AddToCartFormDraft>().control,
    defaultValue: 1,
  });

  return (
    <div className={classNames("scha__included-parts-section", className)}>
      <Headline className="scha__included-parts-section__title">
        {t("page.productDetails.housing.included.title")}
      </Headline>
      <div className="scha__included-parts-section__items">
        {housingProducts.map((housingProduct) => (
          <IncludedPartsItem key={housingProduct.id} housingProduct={housingProduct} kitQuantity={kitQuantity} />
        ))}
      </div>
    </div>
  );
};

interface ItemProps {
  kitQuantity: number;
  housingProduct: HousingProduct;
  className?: string;
}

const IMAGE_WIDTHS: ProductImageWidths = {
  product: "120px",
  manufacturer: "32px",
  brand: "27px",
  gap: "10px",
};

const IncludedPartsItem = ({ housingProduct, className, kitQuantity }: ItemProps) => {
  const { t } = useTranslation();
  const product = housingProduct.product;
  const isHousing = housingProduct.type === HousingProductType.HOUSING;

  let designation = (
    <ProductDesignation
      size={"title-medium"}
      className={"scha__included-parts-item__designation"}
      designation={product.designation}
      isPreferred={product.preferred}
      noCssColor={true}
    />
  );

  if (!isHousing && product.url) {
    designation = (
      <Link variant={LinkType.NAVIGATION} href={product.url} target={"_blank"}>
        {designation}
      </Link>
    );
  }

  const quantity = housingProduct.quantity * kitQuantity;

  return (
    <div
      className={classNames("scha__included-parts-item", className, isHousing && "scha__included-parts-item--housing")}
    >
      <ProductImage className={"scha__included-parts-item__image"} product={product} widths={IMAGE_WIDTHS} />

      {designation}

      <p className="scha__included-parts-item__short-description">
        <Translation {...product.shortDescription} />
      </p>
      <ClampedText className="scha__included-parts-item__description" text={product.description} lines={2} />
      <Parameter label={t("input.quantity")}>{quantity}</Parameter>
    </div>
  );
};
